import { Injectable } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';

import { Breeder, BreederSummary } from './breeder';
import { BaseIndexedDbRepository } from 'libs/arch/src/lib/repositories/indexed-db/indexdb-repository';
import { SynchronizationService } from 'libs/arch/src/lib/repositories/indexed-db/synchronization.service';
import { Actives, ApiService, Filter, NetworkStatusService, PageData, SortOrder } from '@homeleite/arch';
import { map, Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { EMPTY } from '@homeleite/common-utils';

@Injectable({ providedIn: 'root' })
export class BreedersRepository extends BaseIndexedDbRepository<
    BreederSummary,
    Breeder
> {
    protected readonly storeName: string = 'breeders';

    constructor(
        protected override readonly dbService: NgxIndexedDBService,
        protected override readonly synchronizationService: SynchronizationService,
        private readonly apiService: ApiService,
        private readonly networkStatus: NetworkStatusService
    ) {
        super(dbService, synchronizationService);
    }

    protected getResourcePath(): string {
        return 'breeders';
    }

    protected getSearchableFields(): string[] {
        return ['name', 'nickName', 'register', 'producer.name', 'company.name', 'producer.id'];
    }

    protected override mapToEntity(dbItem: Breeder): Breeder {
        return Breeder.fromJson(dbItem);
    }

    protected mapToSummary(item: Breeder): BreederSummary {
        return BreederSummary.createBy(item);
    }

    override find(id: string): Observable<Breeder> {
        if (!this.networkStatus.isOnline()) return super.find(id);

        return this.apiService.root
            .path(this.getResourcePath())
            .path(id)
            .getJson<any>()
            .pipe(
                map((json) => Breeder.fromJson(json)),
                catchError(() => super.find(id))
            );
    }

    override count(query?: string, filters?: Filter[], actives?: Actives): Observable<number> {
        return this.fetchBreeders().pipe(
            map((fullList) => {
                let listToProcess = this.applyActivesFilter(fullList, actives);
                listToProcess = this.applyStructuredFilters(listToProcess, filters);

                if (query && query.trim() !== EMPTY) {
                    const lowerCaseQuery = query.toLowerCase().trim();
                    listToProcess = listToProcess.filter((breeder) =>
                        breeder.name?.toLowerCase().includes(lowerCaseQuery)
                    );
                }

                return listToProcess.length;
            })
        );
    }

    override list(query?: string, filters?: Filter[], sortOrders?: SortOrder[], actives?: Actives): Observable<BreederSummary[]> {
        return this.fetchBreeders().pipe(
            map((fullList) => {
                let listToProcess = this.applyActivesFilter(fullList, actives);
                listToProcess = this.applyStructuredFilters(listToProcess, filters);

                if (query && query.trim() !== EMPTY) {
                    const lowerCaseQuery = query.toLowerCase().trim();
                    listToProcess = listToProcess.filter((breeder) =>
                        breeder.name?.toLowerCase().includes(lowerCaseQuery)
                    );
                }

                return listToProcess.map((item) => this.mapToSummary(item));
            })
        );
    }

    override listFull(query?: string, filters?: Filter[], sortOrders?: SortOrder[], actives?: Actives): Observable<Breeder[]> {
        return this.fetchBreeders().pipe(
            map((fullList) => {
                let listToProcess = this.applyActivesFilter(fullList, actives);
                listToProcess = this.applyStructuredFilters(listToProcess, filters);

                if (query && query.trim() !== EMPTY) {
                    const lowerCaseQuery = query.toLowerCase().trim();
                    listToProcess = listToProcess.filter((breeder) =>
                        breeder.name?.toLowerCase().includes(lowerCaseQuery)
                    );
                }

                return listToProcess;
            })
        );
    }

    override page(pageData: PageData, query?: string, filters?: Filter[], sortOrders?: SortOrder[], actives?: Actives): Observable<BreederSummary[]> {
        return this.fetchBreeders().pipe(
            map((fullList) => {
                let listToProcess = this.applyActivesFilter(fullList, actives);
                listToProcess = this.applyStructuredFilters(listToProcess, filters);

                if (query && query.trim() !== EMPTY) {
                    const lowerCaseQuery = query.toLowerCase().trim();
                    listToProcess = listToProcess.filter((breeder) =>
                        breeder.name?.toLowerCase().includes(lowerCaseQuery)
                    );
                }

                if (pageData?.enabled) {
                    const start = pageData.first;
                    const end = start + pageData.rows;
                    return listToProcess.slice(start, end).map((item) => this.mapToSummary(item));
                }

                return listToProcess.map((item) => this.mapToSummary(item));
            })
        );
    }

    override pageFull(pageData: PageData, query?: string, filters?: Filter[], sortOrders?: SortOrder[], actives?: Actives): Observable<Breeder[]> {
        return this.fetchBreeders().pipe(
            map((fullList) => {
                let listToProcess = this.applyActivesFilter(fullList, actives);
                listToProcess = this.applyStructuredFilters(listToProcess, filters);

                if (query && query.trim() !== EMPTY) {
                    const lowerCaseQuery = query.toLowerCase().trim();
                    listToProcess = listToProcess.filter((breeder) =>
                        breeder.name?.toLowerCase().includes(lowerCaseQuery)
                    );
                }

                if (pageData?.enabled) {
                    const start = pageData.first;
                    const end = start + pageData.rows;
                    return listToProcess.slice(start, end);
                }

                return listToProcess;
            })
        );
    }

    private fetchBreeders(): Observable<Breeder[]> {
        if (!this.networkStatus.isOnline()) {
            return super.listFull();
        }

        return this.apiService.root
            .path(this.getResourcePath())
            .getJson<any[]>()
            .pipe(
                map((response) =>
                    Array.isArray(response) ? response.map((item) => Breeder.fromJson(item)) : []
                ),
                tap((breeders) => {
                    if (!breeders?.length) return;

                    const integrationBreeders = breeders.filter((breeder) => {
                        const { auditData } = breeder;
                        return (
                            auditData?.createdByIntegration || (auditData?.createdByApp && auditData?.modifiedByIntegration)
                        );
                    });

                    if (integrationBreeders.length > 0) {
                        this.saveLocally(integrationBreeders).subscribe({
                            error: (err) => console.error('Falha ao salvar reprodutores em lote:', err)
                        });
                    }
                }),
                catchError(() => super.listFull())
            );
    }
}
